<script>
import Mixin from "@utilities/mixin";
import ServicesMixin from "@components/services/mixin";
import CommentsDialog from "@components/services/CommentsDialog.vue";
import {formatDateString} from "@utilities/helpers.js";


export default {
  name: "ServicesTable",
  mixins: [ServicesMixin, Mixin],
  components: {CommentsDialog},
  props: {
    txns: Array,
    techDrops: Array,
    shops: Array,
  },
  data() {
    return {
      rowsBeingEdited: [],
    }
  },
  computed: {
    managerAccess() {
      return this.isAdmin || this.isManager;
    },
  },
  methods: {
    formatDateString,
    onRowEditSave(event) {
      let {pickup, ...rest} = event.newData;
      this.updateTransaction(rest);
      this.$emit('update');
      this.$toast.add({severity: 'success', summary: 'Success', detail: 'Service Updated', life: 3000});
    },
    instantUpdate(data) {
      const update = {
        ...data,
        pickup: data.pickup ? this.user.ID : null
      }
      this.updateTransaction(update);
      this.$emit('update');
    },
    getOptionLabel(optionID, options = []) {
      const option = options.find(i => i.id === optionID);
      return option ? option.name : "No Status";
    },
    trashTxn(txnID) {
      this.trashTransaction(txnID);
      this.$emit('update');
      this.$toast.add({severity: 'success', summary: 'Success', detail: 'Service Deleted', life: 3000});
    },
    flagClass(data) {
      return {
        'deletion': data.flag === 1,
        'pending-tech': data.flag === 2,
        'pending-non-tech': data.flag === 3
      };
    }
  }
}
</script>

<template>
  <!--Only managers can trigger the editing mode-->
  <!--There's two types of managers, technician (subscriber) managers and runner (contributor) managers-->
  <DataTable
      v-model:editingRows="rowsBeingEdited" :value="txns" editMode="row" dataKey="id" @row-edit-save="onRowEditSave"
      scrollable scrollHeight="calc(100vh - 400px)" class="min-w-[50rem] w-full"
      showGridlines :rowClass="flagClass"
      :pt="{
        table: { style: 'min-width: 50rem' },
        column: {
            bodycell: ({ state }) => {
              if (state['d_editing']) {
                return {
                  style: 'padding: 0;',
                  class: 'whitespace-nowrap'
                }
              } else {
                return {
                  style: 'padding-top: 0; padding-bottom: 0; padding-left: 0.5rem; padding-right: 0.5rem;',
                  class: 'whitespace-nowrap'
                }
              }
            }
        }
    }">
    <Column field="id" header="ID"></Column>
    <Column field="created_at" header="Date">
      <template #body="{ data, field }">
        {{ data[field] instanceof Date ? data[field].toLocaleDateString() : data[field] }}
      </template>
      <template #editor="{ data, field }">
        <DatePicker v-model="data[field]" dateFormat="m/dd/yy" showTime hourFormat="24" class="p-1 w-full min-w-32"
                    fluid/>
      </template>
    </Column>
    <Column field="flag" header="Flag">
      <template #body="slotProps">
        <Select v-model="slotProps.data.flag"
                @change="() => instantUpdate(slotProps.data)"
                :options="flagDrops" optionLabel="name"
                optionValue="id" showClear placeholder="No Flags"/>
      </template>
    </Column>
    <Column :style="{'width': '200px'}" v-if="managerAccess" :rowEditor="true" bodyStyle="text-align:center"></Column>
    <Column>
      <template #body="slotProps">
        <CommentsDialog :txnID="slotProps.data.id"/>
      </template>
      <template v-if="managerAccess" #editor="slotProps">
        <Button icon="pi pi-trash" severity="danger"
                @click="() => trashTxn(slotProps.data.id)"
                text rounded aria-label="Trash"/>
      </template>
    </Column>
    <Column :style="{'width': '200px'}" field="techs" header="Techs">
      <template #editor="{ data, field }">
        <MultiSelect
            v-model="data[field]" display="chip"
            :options="techDrops" optionLabel="name"
            optionValue="id" placeholder="Select Technicians"
            fluid/>
      </template>
      <template #body="slotProps">
        <div>
          <Tag v-for="tID in slotProps.data.techs"
               :key="tID" :value="getOptionLabel(tID, techDrops)"
               severity="secondary" class="me-1 mb-1"/>
        </div>
      </template>
    </Column>
    <Column field="v_year" header="Year">
      <template #editor="{ data, field }">
        <InputText v-model="data[field]" class="p-1 w-full" fluid/>
      </template>
    </Column>
    <Column field="v_model" header="Model">
      <template #editor="{ data, field }">
        <InputText v-model="data[field]" class="p-1 w-full" fluid/>
      </template>
    </Column>
    <Column field="c_name" header="Name">
      <template #editor="{ data, field }">
        <InputText v-model="data[field]" class="p-1" fluid/>
      </template>
    </Column>
    <Column field="service_type" header="Service">
      <template #body="slotProps">
        {{ slotProps.data.service_type }}
      </template>
      <template #editor="{ data, field }">
        <Select v-model="data[field]" :options="sTypeDrops"
                optionLabel="name" optionValue="id"
                placeholder="None Selected" fluid/>
      </template>
    </Column>
    <Column v-if="!isContributor" field="complete" header="Status">
      <template #editor="{ data, field }">
        <Select v-model="data[field]" :options="completeDrops" optionLabel="name" optionValue="id"
                placeholder="None Selected" fluid/>
      </template>
      <template #body="slotProps">
        {{ getOptionLabel(slotProps.data.complete, completeDrops) }}
      </template>
    </Column>
    <Column v-if="isContributor" field="complete" header="Status">
      <template #body="{ data, field }">
        <Select v-model="data[field]"
                @change="() => instantUpdate(data)"
                :options="completeDrops"
                optionLabel="name" optionValue="id"
                placeholder="None Selected" fluid/>
      </template>
    </Column>
    <Column field="service_charge" header="Charge">
      <template #body="{ data, field }">
        {{ formatUSD(data[field]) }}
      </template>
      <template #editor="{ data, field }">
        <InputNumber v-model="data[field]" input-class="p-1" mode="currency" currency="USD" locale="en-US" fluid/>
      </template>
    </Column>
    <Column field="c_phone" header="Phone">
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
      <template #editor="{ data, field }">
        <InputText v-model="data[field]" class="p-1" fluid/>
      </template>
    </Column>
    <Column field="due_date" header="Due">
      <template #body="{ data, field }">
        {{ formatDateString(data[field]) }}
      </template>
      <template #editor="{ data, field }">
        <DatePicker v-model="data[field]" dateFormat="m/dd/yy" class="p-1 w-full min-w-32" fluid/>
      </template>
    </Column>
    <Column v-if="isSubscriber" field="contact_status" header="Called">
      <template #body="{ data, field }">
        <Select v-model="data[field]" @change="instantUpdate(data)" :options="contactDrops" optionLabel="name"
                optionValue="id" placeholder="None Selected" fluid/>
      </template>
    </Column>
    <Column v-if="!isSubscriber" field="contact_status" header="Called">
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
      <template #editor="{ data, field }">
        <Select v-model="data[field]" :options="contactDrops" optionLabel="name" optionValue="id"
                placeholder="None Selected" fluid/>
      </template>
    </Column>
    <Column field="dmv_estimate" header="$DMV">
      <template #body="{ data, field }">
        {{ formatUSD(data[field]) }}
      </template>
      <template #editor="{ data, field }">
        <InputNumber v-model="data[field]" input-class="p-1" mode="currency" currency="USD" locale="en-US" fluid/>
      </template>
    </Column>
    <Column field="total" header="Total">
      <template #body="{ data, field }">
        {{ formatUSD(data[field]) }}
      </template>
      <template #editor="{ data, field }">
        <InputNumber v-model="data[field]" input-class="p-1" mode="currency" currency="USD" locale="en-US" fluid/>
      </template>
    </Column>
    <Column field="owes" header="Owes">
      <template #body="{ data, field }">
        {{ formatUSD(data[field]) }}
      </template>
      <template #editor="{ data, field }">
        <InputNumber v-model="data[field]" input-class="p-1" mode="currency" currency="USD" locale="en-US" fluid/>
      </template>
    </Column>
    <Column field="cash_or_credit" header="$/CC">
      <template #editor="{ data, field }">
        <Select v-model="data[field]" :options="paymentDrops" optionLabel="name" optionValue="id"
                placeholder="None Selected" fluid/>
      </template>
      <template #body="slotProps">
        {{ slotProps.data.cash_or_credit }}
      </template>
    </Column>
    <Column :style="{'width': '200px'}" v-if="!isSubscriber" field="pickup" header="Pickup">
      <template #editor="{ data, field }">
        <ToggleButton v-model="data[field]" @click="instantUpdate(data)"
                      :onLabel="user.display_name" offLabel="Toggle Pickup" onIcon="pi pi-check-circle"
                      aria-label="Is the vehicle picked up?"/>
      </template>
      <template #body="{ data, field }">
        <Tag v-if="data[field]" icon="pi pi-check-circle" :value="data[field].display_name"
             severity="success" class="me-1 mb-1"/>
        <template v-else>
          Not Picked Up
        </template>
      </template>
    </Column>
    <Column :style="{'width': '200px'}" v-if="isSubscriber" field="pickup" header="Pickup">
      <template #body="{ data }">
        <ToggleButton v-model="data['pickup']" @click="instantUpdate(data)"
                      :onLabel="user.display_name" offLabel="Toggle Pickup" onIcon="pi pi-check-circle"
                      aria-label="Is the vehicle picked up?"/>
      </template>
    </Column>
  </DataTable>
</template>

<style>
.deletion {
  background-color: #fdbaba !important
}

.pending-non-tech {
  background-color: #ffedaf !important
}

.pending-tech {
  background-color: #f6c99a !important
}
</style>